import React, { useEffect, useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import pb from '../../lib/pocketbase';
import styles from '../Dashboard.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faEdit, faTrash, faClock, faSearch, faFilter,
    faCalendarWeek, faCalendarDay, faChartBar, faUsers,
    faCheckCircle, faExclamationCircle,
    faList, faCalendar, faUserFriends, faHardHat, faThLarge,
    faFileExcel, faFilePdf, faDownload, faBell, faTimes, faChartLine,
    faPlay, faStopwatch, faPalette, faFilterCircleXmark, faSliders, faCog, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import TimesheetForm from '../../components/dashboard/TimesheetForm';
import TimesheetListView from '../../components/dashboard/TimesheetListView';
import TimesheetCardView from '../../components/dashboard/TimesheetCardView';
import TimesheetEmployeeView from '../../components/dashboard/TimesheetEmployeeView';
import TimesheetChantierView from '../../components/dashboard/TimesheetChantierView';
import TimesheetCalendarView from '../../components/dashboard/TimesheetCalendarView';
import TimesheetStatsModal from '../../components/dashboard/TimesheetStatsModal';
import TimesheetAdvancedSearch from '../../components/dashboard/TimesheetAdvancedSearch';
import TimesheetBulkRejectModal from '../../components/dashboard/TimesheetBulkRejectModal';
import TimesheetPreferencesModal from '../../components/dashboard/TimesheetPreferencesModal';
import TimesheetTimerPanel from '../../components/dashboard/TimesheetTimerPanel';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/fr';
import './Calendar.css';

// Import helpers and export functions
import { getStatusBadge, isWeekend, getHoursColor, getWeek } from '../../utils/timesheetHelpers';
import { exportToExcel, preparePayroll, exportToPDF } from '../../utils/timesheetExport';

const Timesheets = () => {
    const { userRole } = useOutletContext();
    const { primaryColor, setPrimaryColor, themes } = useTheme();
    // Default to 'default' if primaryColor is somehow invalid in the context or local storage
    const activeTheme = themes[primaryColor] ? primaryColor : 'default';
    // Alias setter for compatibility with existing code
    const setActiveTheme = setPrimaryColor;
    moment.locale('fr');
    const localizer = momentLocalizer(moment);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal State
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);

    // View Mode State
    const [viewMode, setViewMode] = useState('list'); // 'list', 'calendar', 'by-employee', 'by-chantier'

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState('THIS_MONTH');
    const [employeeFilter, setEmployeeFilter] = useState('ALL');
    const [chantierFilter, setChantierFilter] = useState('ALL');
    const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, pending_manager, pending_rh, approved, rejected, litige
    // New state for multi-step validation
    const [validationStep, setValidationStep] = useState('pending_manager'); // 'pending_manager', 'pending_rh'
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Export State
    const [showExportMenu, setShowExportMenu] = useState(false);

    // Notification State
    const [showNotifications, setShowNotifications] = useState(false);
    const [dismissedAlerts, setDismissedAlerts] = useState([]);

    // Statistics Modal State
    const [showStats, setShowStats] = useState(false);

    // Timer State
    const [timerRunning, setTimerRunning] = useState(false);
    const [timerStartTime, setTimerStartTime] = useState(null);
    const [timerElapsed, setTimerElapsed] = useState(0);
    const [timerChantier, setTimerChantier] = useState('');
    const [timerDescription, setTimerDescription] = useState('');
    const [showTimerPanel, setShowTimerPanel] = useState(false);

    // Geolocation State (Badgeo)
    const [userLocation, setUserLocation] = useState(null);
    const [locationStatus, setLocationStatus] = useState('idle'); // idle, locating, success, error
    const [nearestSite, setNearestSite] = useState(null);

    // Theme State
    const [showThemeSelector, setShowThemeSelector] = useState(false);
    // REMOVED local activeTheme state, using context instead

    // Advanced Search State
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [advancedFilters, setAdvancedFilters] = useState({
        employee: '',
        chantier: '',
        dateFrom: '',
        dateTo: '',
        hoursMin: '',
        hoursMax: '',
        description: '',
        status: ''
    });

    // User Preferences State
    const [showPreferences, setShowPreferences] = useState(false);
    const [userPreferences, setUserPreferences] = useState(() => {
        const saved = localStorage.getItem('timesheetPreferences');
        return saved ? JSON.parse(saved) : {
            defaultView: 'list',           // 'list', 'employee', 'calendar'
            defaultDateFilter: 'week',     // 'today', 'week', 'month', 'custom'
            showWeekends: true,
            compactMode: false,
            autoRefresh: false,
            refreshInterval: 30,           // seconds
            notificationsEnabled: true,
            defaultTheme: 'default'
        };
    });

    // Bulk Selection State
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [validationComment, setValidationComment] = useState('');

    useEffect(() => {
        loadTimesheets();
    }, [userRole]);

    // Close export menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showExportMenu && !event.target.closest('[data-export-menu]')) {
                setShowExportMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showExportMenu]);

    // Close notifications when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showNotifications && !event.target.closest('[data-notifications-menu]')) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showNotifications]);

    // Close theme selector when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showThemeSelector && !event.target.closest('[data-theme-selector]')) {
                setShowThemeSelector(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showThemeSelector]);


    const loadTimesheets = async () => {
        try {
            const records = await pb.collection('timesheets').getFullList({
                sort: '-date',
                expand: 'worker,chantier'
            });
            setEntries(records);
        } catch (err) {
            console.error("Erreur chargement heures", err);
        } finally {
            setLoading(false);
        }
    };

    // Timer tick effect
    useEffect(() => {
        let interval;
        if (timerRunning && timerStartTime) {
            interval = setInterval(() => {
                setTimerElapsed(Math.floor((Date.now() - timerStartTime) / 1000));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timerRunning, timerStartTime]);

    // Geolocation Helper
    const verifyLocation = () => {
        setLocationStatus('locating');
        if (!navigator.geolocation) {
            setLocationStatus('error');
            toast.error("Géolocalisation non supportée par votre navigateur.");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setUserLocation({ latitude, longitude });
                setLocationStatus('success');

                // Mock: Find nearest site (In real app, calculate distance to sites)
                // For now, if we have chantiers loaded, picking the first one as "Nearest" for demo
                if (chantiers.length > 0) {
                    setNearestSite(chantiers[0]);
                    // toast.success(`📍 Localisé près de : ${chantiers[0]}`);
                }
            },
            (error) => {
                console.error("Geolocation error:", error);
                setLocationStatus('error');
                toast.warning("Impossible de vous géolocaliser. Vérifiez vos paramètres.");
            }
        );
    };

    // Timer functions
    const startTimer = () => {
        verifyLocation(); // Start location verification

        const now = Date.now();
        setTimerStartTime(now);
        setTimerRunning(true);
        setTimerElapsed(0);
        setShowTimerPanel(true);
        toast.success('⏱️ Timer démarré ! (Géolocalisation en cours...)');
    };

    const pauseTimer = () => {
        setTimerRunning(false);
        toast.info('⏸️ Timer mis en pause');
    };

    const resumeTimer = () => {
        // Adjust start time to account for elapsed time
        setTimerStartTime(Date.now() - (timerElapsed * 1000));
        setTimerRunning(true);
        toast.success('▶️ Timer repris');
    };

    const stopTimer = () => {
        setTimerRunning(false);
        setTimerStartTime(null);
        setTimerElapsed(0);
        setTimerChantier('');
        setTimerDescription('');

        // Reset location
        setUserLocation(null);
        setLocationStatus('idle');
        setNearestSite(null);

        setShowTimerPanel(false);
        toast.info('⏹️ Timer arrêté');
    };

    const saveTimer = async () => {
        if (timerElapsed === 0) {
            toast.error('Le timer n\'a pas encore démarré');
            return;
        }

        if (!timerChantier) {
            toast.error('Veuillez sélectionner un chantier');
            return;
        }

        const hours = parseFloat((timerElapsed / 3600).toFixed(2));

        // Append location info to description
        let finalDescription = timerDescription || `Travail enregistré via timer`;
        if (userLocation) {
            finalDescription += `\n📍 GPS: ${userLocation.latitude.toFixed(4)}, ${userLocation.longitude.toFixed(4)}`;
            if (nearestSite) finalDescription += ` (Proche de: ${nearestSite})`;
        } else if (locationStatus === 'error') {
            finalDescription += `\n⚠️ Géolocalisation échouée`;
        }

        try {
            const data = {
                date: new Date().toISOString().split('T')[0],
                hours: hours,
                description: finalDescription,
                worker: pb.authStore.model?.id,
                chantier: timerChantier,
                status: 'pending_manager' // Default to pending_manager
            };

            await pb.collection('timesheets').create(data);
            toast.success(`✅ ${hours}h enregistrées avec succès !`);

            // Reset timer
            stopTimer();
            loadTimesheets();
        } catch (err) {
            console.error('Error saving timer:', err);
            toast.error('Erreur lors de l\'enregistrement');
        }
    };

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Apply theme to CSS variables - MOVED TO CONTEXT
    const applyTheme = (themeName) => {
        setActiveTheme(themeName);
        const theme = themes[themeName];
        if (theme) {
            toast.success(`${theme.icon} Thème "${theme.name}" appliqué !`);
        }
    };

    // User Preferences Functions
    const savePreferences = (newPreferences) => {
        const updated = { ...userPreferences, ...newPreferences };
        setUserPreferences(updated);
        localStorage.setItem('timesheetPreferences', JSON.stringify(updated));
        toast.success('✅ Préférences enregistrées !');
    };

    const resetPreferences = () => {
        const defaultPrefs = {
            defaultView: 'list',
            defaultDateFilter: 'week',
            showWeekends: true,
            compactMode: false,
            autoRefresh: false,
            refreshInterval: 30,
            notificationsEnabled: true,
            defaultTheme: 'default'
        };
        setUserPreferences(defaultPrefs);
        localStorage.setItem('timesheetPreferences', JSON.stringify(defaultPrefs));

        // Apply default view and theme
        setViewMode(defaultPrefs.defaultView);
        setDateFilter(defaultPrefs.defaultDateFilter);
        setActiveTheme(defaultPrefs.defaultTheme); // Use global setter

        toast.info('🔄 Préférences réinitialisées');
    };

    const applyUserPreferences = () => {
        // Apply saved preferences on mount
        if (userPreferences.defaultView) {
            setViewMode(userPreferences.defaultView);
        }
        if (userPreferences.defaultDateFilter) {
            setDateFilter(userPreferences.defaultDateFilter);
        }
        if (userPreferences.defaultTheme && userPreferences.defaultTheme !== activeTheme) {
            setActiveTheme(userPreferences.defaultTheme);
        }
    };

    // REMOVED applyTheme on mount effect as context handles it

    // Apply user preferences on mount
    useEffect(() => {
        applyUserPreferences();
    }, []); // Only on mount


    // Get unique employees and chantiers for filters
    const employees = useMemo(() => {
        const unique = [...new Set(entries.map(e => e.expand?.worker?.name || e.expand?.worker?.username).filter(Boolean))];
        return unique.sort();
    }, [entries]);

    const chantiers = useMemo(() => {
        const unique = [...new Set(entries.map(e => e.expand?.chantier?.name).filter(Boolean))];
        return unique.sort();
    }, [entries]);

    // Date range filtering logic
    const getDateRange = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1);
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

        switch (dateFilter) {
            case 'TODAY':
                return { start: new Date(today.setHours(0, 0, 0, 0)), end: new Date(today.setHours(23, 59, 59, 999)) };
            case 'THIS_WEEK':
                return { start: startOfWeek, end: today };
            case 'THIS_MONTH':
                return { start: startOfMonth, end: today };
            case 'CUSTOM':
                return {
                    start: customStartDate ? new Date(customStartDate) : null,
                    end: customEndDate ? new Date(customEndDate) : null
                };
            case 'ALL':
            default:
                return { start: null, end: null };
        }
    };

    // Filter entries
    const filteredEntries = useMemo(() => {
        const { start, end } = getDateRange();

        return entries.filter(entry => {
            const entryDate = new Date(entry.date);
            const matchesSearch =
                entry.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (entry.expand?.worker?.name || entry.expand?.worker?.username)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                entry.expand?.chantier?.name?.toLowerCase().includes(searchTerm.toLowerCase());

            const employeeName = entry.expand?.worker?.name || entry.expand?.worker?.username;
            const matchesEmployee = employeeFilter === 'ALL' || employeeName === employeeFilter;
            const matchesChantier = chantierFilter === 'ALL' || entry.expand?.chantier?.name === chantierFilter;
            const matchesStatus = statusFilter === 'ALL' || (entry.status || 'pending') === statusFilter;

            const matchesDate = (!start || entryDate >= start) && (!end || entryDate <= end);

            // Advanced filters
            const employeeNameAdv = entry.expand?.worker?.name || entry.expand?.worker?.username;
            const matchesAdvEmployee = !advancedFilters.employee || employeeNameAdv === advancedFilters.employee;
            const matchesAdvChantier = !advancedFilters.chantier || entry.expand?.chantier?.name === advancedFilters.chantier;
            const matchesAdvDateFrom = !advancedFilters.dateFrom || new Date(entry.date) >= new Date(advancedFilters.dateFrom);
            const matchesAdvDateTo = !advancedFilters.dateTo || new Date(entry.date) <= new Date(advancedFilters.dateTo);
            const matchesAdvHoursMin = !advancedFilters.hoursMin || entry.hours >= parseFloat(advancedFilters.hoursMin);
            const matchesAdvHoursMax = !advancedFilters.hoursMax || entry.hours <= parseFloat(advancedFilters.hoursMax);
            const matchesAdvDescription = !advancedFilters.description || entry.description?.toLowerCase().includes(advancedFilters.description.toLowerCase());
            const matchesAdvStatus = !advancedFilters.status || (entry.status || 'pending') === advancedFilters.status;

            return matchesSearch && matchesEmployee && matchesChantier && matchesStatus && matchesDate &&
                matchesAdvEmployee && matchesAdvChantier && matchesAdvDateFrom && matchesAdvDateTo &&
                matchesAdvHoursMin && matchesAdvHoursMax && matchesAdvDescription && matchesAdvStatus;
        });
    }, [entries, searchTerm, employeeFilter, chantierFilter, statusFilter, dateFilter, customStartDate, customEndDate, advancedFilters]);

    // Group entries by employee
    const groupedByEmployee = useMemo(() => {
        const grouped = {};
        filteredEntries.forEach(entry => {
            const empName = entry.expand?.worker?.name || entry.expand?.worker?.username || 'Inconnu';
            if (!grouped[empName]) {
                grouped[empName] = { entries: [], totalHours: 0 };
            }
            grouped[empName].entries.push(entry);
            grouped[empName].totalHours += entry.hours || 0;
        });
        return grouped;
    }, [filteredEntries]);

    // Group entries by chantier
    const groupedByChantier = useMemo(() => {
        const grouped = {};
        filteredEntries.forEach(entry => {
            const chantierName = entry.expand?.chantier?.name || 'Non assigné';
            if (!grouped[chantierName]) {
                grouped[chantierName] = { entries: [], totalHours: 0 };
            }
            grouped[chantierName].entries.push(entry);
            grouped[chantierName].totalHours += entry.hours || 0;
        });
        return grouped;
    }, [filteredEntries]);

    // NEW: Grouped by Day for "One line per day" requirement
    const groupedByDayEntries = useMemo(() => {
        const grouped = {};
        filteredEntries.forEach(entry => {
            const date = entry.date?.substring(0, 10);
            const workerId = entry.worker;
            const key = `${date}_${workerId}`;

            if (!grouped[key]) {
                grouped[key] = {
                    id: entry.id,
                    date: date,
                    worker: entry.worker,
                    workerName: entry.expand?.worker?.name || entry.expand?.worker?.username || 'Inconnu',
                    chantier: entry.expand?.chantier?.name || 'Général',
                    hours: 0,
                    status: entry.status || 'pending_manager',
                    description: '',
                    items: [], // Individual records
                    expand: entry.expand
                };
            }

            const group = grouped[key];
            group.hours = parseFloat((group.hours + (parseFloat(entry.hours) || 0)).toFixed(1));
            group.items.push(entry);

            // Status Priority: highest priority wins
            const statusPriority = { 'litige': 5, 'rejected': 4, 'pending_rh': 3, 'pending_manager': 2, 'approved': 1, '': 2, null: 2 };
            if (statusPriority[entry.status] > statusPriority[group.status]) {
                group.status = entry.status || 'pending_manager';
            }

            if (entry.description) {
                if (!entry.description.includes('Session')) {
                    group.description += (group.description ? ' | ' : '') + entry.description;
                }
            }
        });
        return Object.values(grouped).sort((a, b) => b.date.localeCompare(a.date));
    }, [filteredEntries]);

    // Calculate KPIs
    const kpis = useMemo(() => {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1);
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

        const weekEntries = entries.filter(e => new Date(e.date) >= startOfWeek);
        const monthEntries = entries.filter(e => new Date(e.date) >= startOfMonth);

        const totalHoursWeek = weekEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
        const totalHoursMonth = monthEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
        const totalHoursFiltered = filteredEntries.reduce((sum, e) => sum + (e.hours || 0), 0);

        const avgHoursPerDay = monthEntries.length > 0
            ? (totalHoursMonth / new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()).toFixed(1)
            : 0;

        return {
            totalHoursWeek,
            totalHoursMonth,
            totalHoursFiltered,
            avgHoursPerDay,
            entriesCount: filteredEntries.length
        };
    }, [entries, filteredEntries]);

    // Calculate Alerts/Notifications
    const alerts = useMemo(() => {
        const alertsList = [];

        // 1. Pending validations
        const pendingEntries = entries.filter(e => !e.status || e.status === 'pending');
        if (pendingEntries.length > 0) {
            alertsList.push({
                id: 'pending-validations',
                type: 'warning',
                title: 'Heures en attente de validation',
                message: `${pendingEntries.length} entrée(s) en attente de validation`,
                count: pendingEntries.length,
                icon: faExclamationCircle,
                color: '#f59e0b',
                action: () => setStatusFilter('pending')
            });
        }

        // 2. Excessive hours (>10h in a single day)
        const excessiveHours = entries.filter(e => e.hours > 10);
        if (excessiveHours.length > 0) {
            alertsList.push({
                id: 'excessive-hours',
                type: 'danger',
                title: 'Heures excessives détectées',
                message: `${excessiveHours.length} entrée(s) avec plus de 10h`,
                count: excessiveHours.length,
                icon: faExclamationCircle,
                color: '#ef4444',
                action: () => {
                    setSearchTerm('');
                    setDateFilter('ALL');
                }
            });
        }

        // 3. Weekend work
        const weekendEntries = entries.filter(e => {
            const date = new Date(e.date);
            const day = date.getDay();
            return (day === 0 || day === 6);
        });
        if (weekendEntries.length > 0 && weekendEntries.some(e => !e.status || e.status === 'pending')) {
            const pendingWeekend = weekendEntries.filter(e => !e.status || e.status === 'pending');
            alertsList.push({
                id: 'weekend-work',
                type: 'info',
                title: 'Travail en week-end',
                message: `${pendingWeekend.length} entrée(s) de week-end à valider`,
                count: pendingWeekend.length,
                icon: faCalendarDay,
                color: '#3b82f6',
                action: () => setStatusFilter('pending')
            });
        }

        // 4. Old pending entries (>7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const oldPending = entries.filter(e => {
            const entryDate = new Date(e.date);
            return (!e.status || e.status === 'pending') && entryDate < sevenDaysAgo;
        });
        if (oldPending.length > 0) {
            alertsList.push({
                id: 'old-pending',
                type: 'warning',
                title: 'Anciennes entrées en attente',
                message: `${oldPending.length} entrée(s) de plus de 7 jours non validées`,
                count: oldPending.length,
                icon: faClock,
                color: '#f59e0b',
                action: () => {
                    setStatusFilter('pending');
                    setDateFilter('ALL');
                }
            });
        }

        // Filter out dismissed alerts
        return alertsList.filter(alert => !dismissedAlerts.includes(alert.id));
    }, [entries, dismissedAlerts]);

    const dismissAlert = (alertId) => {
        setDismissedAlerts(prev => [...prev, alertId]);
        toast.info('Alerte masquée');
    };

    // Advanced Search Functions
    const applyAdvancedFilters = () => {
        setShowAdvancedSearch(false);
        const count = Object.values(advancedFilters).filter(v => v !== '').length;
        if (count > 0) {
            toast.success(`✅ ${count} filtre(s) appliqué(s)`);
        }
    };

    const resetAdvancedFilters = () => {
        setAdvancedFilters({
            employee: '',
            chantier: '',
            dateFrom: '',
            dateTo: '',
            hoursMin: '',
            hoursMax: '',
            description: '',
            status: ''
        });
        toast.info('🔄 Filtres réinitialisés');
    };

    const activeFiltersCount = useMemo(() => {
        return Object.values(advancedFilters).filter(v => v !== '').length;
    }, [advancedFilters]);

    // Prepare Chart Data
    const chartData = useMemo(() => {
        // 1. Hours by Employee
        const employeeStats = {};
        entries.forEach(entry => {
            const employeeName = entry.expand?.worker?.name || entry.expand?.worker?.username || 'Inconnu';
            if (!employeeStats[employeeName]) {
                employeeStats[employeeName] = {
                    name: employeeName,
                    hours: 0,
                    entries: 0
                };
            }
            employeeStats[employeeName].hours += entry.hours || 0;
            employeeStats[employeeName].entries += 1;
        });
        const employeeData = Object.values(employeeStats).sort((a, b) => b.hours - a.hours);

        // 2. Hours by Chantier (Pie Chart)
        const chantierStats = {};
        entries.forEach(entry => {
            const chantierName = entry.expand?.chantier?.name || 'Non assigné';
            if (!chantierStats[chantierName]) {
                chantierStats[chantierName] = 0;
            }
            chantierStats[chantierName] += entry.hours || 0;
        });
        const chantierData = Object.entries(chantierStats).map(([name, hours]) => ({
            name,
            value: hours
        })).sort((a, b) => b.value - a.value);

        // 3. Daily Trend (Last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const dailyStats = {};

        entries.forEach(entry => {
            const entryDate = new Date(entry.date);
            if (entryDate >= thirtyDaysAgo) {
                const dateKey = entryDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                if (!dailyStats[dateKey]) {
                    dailyStats[dateKey] = { date: dateKey, hours: 0, entries: 0 };
                }
                dailyStats[dateKey].hours += entry.hours || 0;
                dailyStats[dateKey].entries += 1;
            }
        });
        const dailyData = Object.values(dailyStats).sort((a, b) => {
            const [dayA, monthA] = a.date.split('/');
            const [dayB, monthB] = b.date.split('/');
            return new Date(2024, monthA - 1, dayA) - new Date(2024, monthB - 1, dayB);
        });

        // 4. Weekly Distribution (by day of week)
        const weekDayStats = {
            'Lun': 0, 'Mar': 0, 'Mer': 0, 'Jeu': 0, 'Ven': 0, 'Sam': 0, 'Dim': 0
        };
        const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

        entries.forEach(entry => {
            const entryDate = new Date(entry.date);
            const dayName = dayNames[entryDate.getDay()];
            weekDayStats[dayName] += entry.hours || 0;
        });
        const weekData = Object.entries(weekDayStats).map(([day, hours]) => ({
            day,
            hours: Math.round(hours * 10) / 10
        }));

        return {
            employeeData,
            chantierData,
            dailyData,
            weekData
        };
    }, [entries]);

    const handleCreate = () => {
        setEditingEntry(null);
        setIsFormOpen(true);
    };

    const handleEdit = (entryOrGroup) => {
        setEditingEntry(entryOrGroup.items ? entryOrGroup.items[0] : entryOrGroup);
        setIsFormOpen(true);
    };

    const handleDelete = async (idOrGroup) => {
        if (!window.confirm("Supprimer : êtes-vous sûr ?")) return;
        try {
            const isGroup = typeof idOrGroup === 'object' && idOrGroup.items;
            const targetIds = isGroup ? idOrGroup.items.map(it => it.id) : [typeof idOrGroup === 'object' ? idOrGroup.id : idOrGroup];
            await Promise.all(targetIds.map(id => pb.collection('timesheets').delete(id)));
            toast.success("Supprimé");
            loadTimesheets();
        } catch (err) {
            toast.error("Erreur à la suppression");
        }
    };

    const handleSave = () => {
        setIsFormOpen(false);
        loadTimesheets();
    };


    // Bulk validation handlers
    const handleBulkApprove = async () => {
        try {
            let promotedCount = 0;
            let approvedCount = 0;

            for (const id of selectedEntries) {
                const group = groupedByDayEntries.find(e => e.id === id);
                const targetIds = group && group.items ? group.items.map(it => it.id) : [id];
                const currentStatus = group ? group.status : 'pending_manager';
                let newStatus = currentStatus;

                if (currentStatus === 'pending' || currentStatus === 'pending_manager' || currentStatus === 'litige') {
                    newStatus = 'pending_rh';
                    promotedCount += targetIds.length;
                } else if (currentStatus === 'pending_rh') {
                    newStatus = 'approved';
                    approvedCount += targetIds.length;
                }

                if (newStatus !== currentStatus) {
                    await Promise.all(targetIds.map(targetId => pb.collection('timesheets').update(targetId, { status: newStatus })));
                }
            }

            if (promotedCount > 0) toast.success(`${promotedCount} envoyées aux RH`);
            if (approvedCount > 0) toast.success(`${approvedCount} validées définitivement`);

            setSelectedEntries([]);
            loadTimesheets();
        } catch (err) {
            toast.error('Erreur lors de la validation');
        }
    };

    const handleBulkReject = () => {
        if (selectedEntries.length === 0) return;
        setShowValidationModal(true);
    };

    const confirmBulkReject = async () => {
        try {
            for (const id of selectedEntries) {
                const group = groupedByDayEntries.find(e => e.id === id);
                const targetIds = group && group.items ? group.items.map(it => it.id) : [id];

                await Promise.all(targetIds.map(targetId => pb.collection('timesheets').update(targetId, {
                    status: 'rejected',
                    validation_comment: validationComment
                })));
            }
            toast.success(`${selectedEntries.length} entrée(s) rejetée(s)`);
            setSelectedEntries([]);
            setShowValidationModal(false);
            setValidationComment('');
            loadTimesheets();
        } catch (err) {
            toast.error('Erreur lors du rejet');
        }
    };

    const handleSingleApprove = async (idOrGroup) => {
        try {
            const isGroup = typeof idOrGroup === 'object' && idOrGroup.items;
            const targetIds = isGroup ? idOrGroup.items.map(it => it.id) : [idOrGroup];

            const firstEntry = isGroup ? idOrGroup.items[0] : entries.find(e => e.id === idOrGroup);
            const currentStatus = firstEntry?.status || 'pending_manager';
            let newStatus = currentStatus;
            let message = '';

            // Workflow Logic
            if (currentStatus === 'pending' || currentStatus === 'pending_manager' || currentStatus === 'litige') {
                newStatus = 'pending_rh';
                message = isGroup ? 'Journée envoyée aux RH' : 'Validé par Manager -> En attente RH';
            } else if (currentStatus === 'pending_rh') {
                newStatus = 'approved';
                message = isGroup ? 'Journée validée définitivement' : 'Validé définitivement (RH)';
            } else {
                return; // Already approved or rejected
            }

            await Promise.all(targetIds.map(id => pb.collection('timesheets').update(id, { status: newStatus })));
            toast.success(message);
            loadTimesheets();
        } catch (err) {
            toast.error('Erreur lors de la validation');
        }
    };

    const handleSingleReject = async (idOrGroup) => {
        const isGroup = typeof idOrGroup === 'object' && idOrGroup.items;
        const targetIds = isGroup ? idOrGroup.items.map(it => it.id) : [idOrGroup];

        if (!window.confirm(`Rejeter ${isGroup ? 'cette journée' : 'cette entrée'} ?`)) return;

        try {
            await Promise.all(targetIds.map(id => pb.collection('timesheets').update(id, { status: 'rejected' })));
            toast.success(isGroup ? 'Journée rejetée' : 'Entrée rejetée');
            loadTimesheets();
        } catch (err) {
            toast.error('Erreur lors du rejet');
        }
    };


    const toggleSelectEntry = (id) => {
        setSelectedEntries(prev =>
            prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedEntries.length === filteredEntries.length) {
            setSelectedEntries([]);
        } else {
            setSelectedEntries(filteredEntries.map(e => e.id));
        }
    };

    // Export Functions
    const handleExportExcel = () => {
        try {
            exportToExcel(filteredEntries);
            toast.success('Export Excel réussi !');
            setShowExportMenu(false);
        } catch (err) {
            toast.error('Erreur lors de l\'export Excel');
        }
    };

    const handlePreparePayroll = () => {
        try {
            const result = preparePayroll(filteredEntries, dateFilter);
            if (result.empty) {
                toast.warning("Aucune donnée validée ('approved') à exporter pour la paie.");
            } else if (result.success) {
                toast.success(`📤 Export Paie généré avec succès ! (${result.count} employés)`);
                setShowExportMenu(false);
            }
        } catch (err) {
            toast.error('Erreur lors de la préparation paie');
        }
    };

    const handleExportPDF = () => {
        try {
            exportToPDF(filteredEntries, {
                date: dateFilter,
                employee: employeeFilter,
                chantier: chantierFilter,
                status: statusFilter
            });
            toast.success('Export PDF réussi !');
            setShowExportMenu(false);
        } catch (err) {
            toast.error('Erreur lors de l\'export PDF');
        }
    };


    return (
        <div className={styles.dataSection}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                {/* TITLE - LEFT */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{ margin: 0 }}><FontAwesomeIcon icon={faClock} /> Relevé d'Heures</h3>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px' }} />
                        Période: {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    </div>
                </div>

                {/* ACTIONS & VALIDATION - RIGHT */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'flex-end' }}>
                    {/* Action Buttons Group */}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {/* Statistics Button */}
                        <button
                            className={styles.addButton}
                            onClick={() => setShowStats(true)}
                            style={{
                                background: '#8b5cf6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '40px',
                                padding: '10px'
                            }}
                            title="Voir les statistiques"
                        >
                            <FontAwesomeIcon icon={faChartLine} />
                        </button>

                        {/* Theme Selector */}
                        <div style={{ position: 'relative' }} data-theme-selector>
                            <button
                                className={styles.addButton}
                                onClick={() => setShowThemeSelector(!showThemeSelector)}
                                style={{
                                    background: showThemeSelector ? themes[activeTheme].primary : 'var(--color-primary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '40px',
                                    padding: '10px'
                                }}
                                title={"Thème: " + themes[activeTheme].name}
                            >
                                <FontAwesomeIcon icon={faPalette} />
                            </button>

                            {/* Theme Dropdown */}
                            {showThemeSelector && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: '8px',
                                    background: 'var(--color-surface)',
                                    borderRadius: 'var(--border-radius-lg)',
                                    boxShadow: 'var(--shadow-xl)',
                                    border: '1px solid var(--color-light-gray)',
                                    minWidth: '280px',
                                    zIndex: 1000,
                                    padding: '1rem'
                                }}>
                                    <div style={{
                                        marginBottom: '1rem',
                                        paddingBottom: '0.75rem',
                                        borderBottom: '1px solid var(--color-light-gray)'
                                    }}>
                                        <h4 style={{
                                            margin: 0,
                                            fontSize: '0.95rem',
                                            fontWeight: '700',
                                            color: 'var(--color-text)'
                                        }}>
                                            <FontAwesomeIcon icon={faPalette} style={{ marginRight: '8px' }} />
                                            Choisir un thème
                                        </h4>
                                    </div>

                                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                                        {Object.entries(themes).map(([key, theme]) => (
                                            <button
                                                key={key}
                                                onClick={() => {
                                                    applyTheme(key);
                                                    setShowThemeSelector(false);
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '0.75rem 1rem',
                                                    background: activeTheme === key ? 'var(--color-background)' : 'transparent',
                                                    border: activeTheme === key ? `2px solid ${theme.primary}` : '2px solid var(--color-light-gray)',
                                                    borderRadius: 'var(--border-radius-md)',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    width: '100%'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (activeTheme !== key) {
                                                        e.currentTarget.style.borderColor = theme.primary;
                                                        e.currentTarget.style.background = 'var(--color-background)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (activeTheme !== key) {
                                                        e.currentTarget.style.borderColor = 'var(--color-light-gray)';
                                                        e.currentTarget.style.background = 'transparent';
                                                    }
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <span style={{ fontSize: '1.5rem' }}>{theme.icon}</span>
                                                    <span style={{
                                                        fontWeight: activeTheme === key ? '600' : '500',
                                                        color: 'var(--color-text)'
                                                    }}>
                                                        {theme.name}
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    <div style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        background: theme.primary,
                                                        border: '2px solid white',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                    }} />
                                                    <div style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        background: theme.accent,
                                                        border: '2px solid white',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                    }} />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Preferences Button */}
                        <button
                            className={styles.addButton}
                            onClick={() => setShowPreferences(true)}
                            style={{
                                background: '#6b7280',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '40px',
                                padding: '10px'
                            }}
                            title="Préférences"
                        >
                            <FontAwesomeIcon icon={faCog} />
                        </button>

                        {/* Export Menu */}
                        <div style={{ position: 'relative' }} data-export-menu>
                            <button
                                className={styles.addButton}
                                onClick={() => setShowExportMenu(!showExportMenu)}
                                style={{
                                    background: showExportMenu ? 'var(--color-secondary)' : 'var(--color-primary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '10px 20px',
                                    gap: '8px',
                                    fontWeight: '600'
                                }}
                                title="Exporter"
                            >
                                <FontAwesomeIcon icon={faDownload} /> Exporter
                            </button>
                            {showExportMenu && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: '8px',
                                    background: 'var(--color-surface)',
                                    borderRadius: 'var(--border-radius-md)',
                                    boxShadow: 'var(--shadow-lg)',
                                    border: '1px solid var(--color-light-gray)',
                                    minWidth: '180px',
                                    zIndex: 100,
                                    overflow: 'hidden'
                                }}>
                                    <button
                                        onClick={handleExportExcel}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: 'none',
                                            background: 'transparent',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            color: 'var(--color-text)',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(164, 198, 57, 0.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <FontAwesomeIcon icon={faFileExcel} style={{ color: '#10b981', fontSize: '1.2rem' }} />
                                        Export Excel
                                    </button>
                                    <div style={{ height: '1px', background: 'var(--color-light-gray)', margin: '0 8px' }} />
                                    <button
                                        onClick={handleExportPDF}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: 'none',
                                            background: 'transparent',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            color: 'var(--color-text)',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <FontAwesomeIcon icon={faFilePdf} style={{ color: '#ef4444', fontSize: '1.2rem' }} />
                                        Export PDF
                                    </button>
                                    <div style={{ height: '1px', background: 'var(--color-light-gray)', margin: '0 8px' }} />
                                    <button
                                        onClick={handlePreparePayroll}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: 'none',
                                            background: 'transparent',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            color: 'var(--color-text)',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <FontAwesomeIcon icon={faFileExcel} style={{ color: '#3b82f6', fontSize: '1.2rem' }} />
                                        Préparation Paie
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Validation Quick Access (Admin Only) */}
                    {userRole === 'admin' && alerts.some(a => a.id === 'pending-validations') && (
                        <div style={{
                            background: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid rgba(245, 158, 11, 0.2)',
                            padding: '10px 20px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px',
                            boxShadow: 'var(--shadow-sm)'
                        }}>
                            <div style={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FontAwesomeIcon icon={faExclamationCircle} />
                                <strong style={{ fontSize: '0.9rem' }}>{entries.filter(e => !e.status || e.status === 'pending').length} entrées en attente</strong>
                            </div>
                            <button
                                onClick={() => {
                                    setStatusFilter('pending');
                                    setViewMode('list');
                                }}
                                style={{
                                    background: '#f59e0b',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                Accéder au Validation Hub
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Validation Workflow Filter */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '1.5rem', overflowX: 'auto', paddingBottom: '10px' }}>
                <button
                    onClick={() => setStatusFilter('ALL')}
                    style={{
                        padding: '10px 20px', borderRadius: '30px', border: 'none', cursor: 'pointer',
                        background: statusFilter === 'ALL' ? 'var(--color-primary, #1e293b)' : 'var(--color-surface, white)',
                        color: statusFilter === 'ALL' ? 'white' : 'var(--color-text-light, #64748b)',
                        fontWeight: '600', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', whiteSpace: 'nowrap'
                    }}
                >
                    Tout voir
                </button>
                <button
                    onClick={() => setStatusFilter('pending_manager')}
                    style={{
                        padding: '10px 20px', borderRadius: '30px', border: 'none', cursor: 'pointer',
                        background: statusFilter === 'pending_manager' ? '#f59e0b' : 'var(--color-surface, white)',
                        color: statusFilter === 'pending_manager' ? 'white' : 'var(--color-text-light, #64748b)',
                        fontWeight: '600', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', whiteSpace: 'nowrap'
                    }}
                >
                    <FontAwesomeIcon icon={faExclamationCircle} /> Attente Manager
                </button>
                <button
                    onClick={() => setStatusFilter('pending_rh')}
                    style={{
                        padding: '10px 20px', borderRadius: '30px', border: 'none', cursor: 'pointer',
                        background: statusFilter === 'pending_rh' ? '#3b82f6' : 'var(--color-surface, white)',
                        color: statusFilter === 'pending_rh' ? 'white' : 'var(--color-text-light, #64748b)',
                        fontWeight: '600', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', whiteSpace: 'nowrap'
                    }}
                >
                    <FontAwesomeIcon icon={faUserFriends} /> Attente RH
                </button>
                <button
                    onClick={() => setStatusFilter('approved')}
                    style={{
                        padding: '10px 20px', borderRadius: '30px', border: 'none', cursor: 'pointer',
                        background: statusFilter === 'approved' ? '#10b981' : 'var(--color-surface, white)',
                        color: statusFilter === 'approved' ? 'white' : 'var(--color-text-light, #64748b)',
                        fontWeight: '600', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', whiteSpace: 'nowrap'
                    }}
                >
                    <FontAwesomeIcon icon={faCheckCircle} /> Validé
                </button>
            </div>

            {/* Monthly Summary Dashboard */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                <div style={{ background: 'var(--color-surface, white)', padding: '1.5rem', borderRadius: '16px', boxShadow: 'var(--shadow-md)', borderLeft: '4px solid #a4c639' }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-light, #64748b)', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Heures du Mois</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                        <span style={{ fontSize: '2rem', fontWeight: '900', color: 'var(--color-primary)' }}>{kpis.totalHoursMonth}h</span>
                        <span style={{ fontSize: '0.85rem', color: '#10b981', marginBottom: '5px', fontWeight: 'bold' }}>
                            <FontAwesomeIcon icon={faChartLine} /> +5.2% vs M-1
                        </span>
                    </div>
                    <div style={{ width: '100%', height: '6px', backgroundColor: '#f1f5f9', borderRadius: '3px', marginTop: '12px', overflow: 'hidden' }}>
                        <div style={{ width: '75%', height: '100%', backgroundColor: '#a4c639' }} />
                    </div>
                </div>

                <div style={{ background: 'var(--color-surface, white)', padding: '1.5rem', borderRadius: '16px', boxShadow: 'var(--shadow-md)', borderLeft: '4px solid #3b82f6' }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-light, #64748b)', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Volume Validé</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                        <span style={{ fontSize: '2rem', fontWeight: '900', color: '#3b82f6' }}>
                            {Math.round((entries.filter(e => e.status === 'approved').length / (entries.length || 1)) * 100)}%
                        </span>
                        <span style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '5px' }}>
                            {entries.filter(e => e.status === 'approved').length} / {entries.length} validées
                        </span>
                    </div>
                    <div style={{ width: '100%', height: '6px', backgroundColor: '#f1f5f9', borderRadius: '3px', marginTop: '12px', overflow: 'hidden' }}>
                        <div style={{
                            width: `${(entries.filter(e => e.status === 'approved').length / (entries.length || 1)) * 100}%`,
                            height: '100%',
                            backgroundColor: '#3b82f6'
                        }} />
                    </div>
                </div>

                <div style={{ background: 'var(--color-surface, white)', padding: '1.5rem', borderRadius: '16px', boxShadow: 'var(--shadow-md)', borderLeft: '4px solid #f59e0b' }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-light, #64748b)', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Alertes en cours</div>
                    <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: '900', fontSize: '1.5rem', color: '#f59e0b' }}>{entries.filter(e => e.hours > 10).length}</div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--color-text-light, #64748b)' }}>H. Excessives</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: '900', fontSize: '1.5rem', color: '#ef4444' }}>{entries.filter(e => isWeekend(e.date)).length}</div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--color-text-light, #64748b)' }}>Week-end</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.sectionHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <h4 style={{ margin: 0, fontWeight: '800' }}>Tableau de bord</h4>
                </div>
                <div className={styles.headerActions}>


                    {/* Notifications Bell */}
                    <div style={{ position: 'relative' }} data-notifications-menu>
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            style={{
                                position: 'relative',
                                background: showNotifications ? 'var(--color-primary)' : 'var(--color-surface)',
                                border: '1px solid var(--color-light-gray)',
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                color: showNotifications ? 'white' : 'var(--color-text)'
                            }}
                        >
                            <FontAwesomeIcon icon={faBell} size="lg" />
                            {alerts.length > 0 && (
                                <span style={{
                                    position: 'absolute',
                                    top: '-4px',
                                    right: '-4px',
                                    background: '#ef4444',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    border: '2px solid var(--color-background)'
                                }}>
                                    {alerts.length}
                                </span>
                            )}
                        </button>

                        {/* Notifications Panel */}
                        {showNotifications && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: '8px',
                                background: 'var(--color-surface)',
                                borderRadius: 'var(--border-radius-lg)',
                                boxShadow: 'var(--shadow-xl)',
                                border: '1px solid var(--color-light-gray)',
                                minWidth: '380px',
                                maxWidth: '450px',
                                maxHeight: '500px',
                                overflowY: 'auto',
                                zIndex: 1000
                            }}>
                                <div style={{
                                    padding: '1rem 1.5rem',
                                    borderBottom: '1px solid var(--color-light-gray)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    position: 'sticky',
                                    top: 0,
                                    background: 'var(--color-surface)',
                                    zIndex: 1
                                }}>
                                    <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '700' }}>
                                        <FontAwesomeIcon icon={faBell} style={{ marginRight: '8px' }} />
                                        Notifications ({alerts.length})
                                    </h4>
                                    <button
                                        onClick={() => setShowNotifications(false)}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--color-text-light)',
                                            fontSize: '1.2rem'
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                                <div style={{ padding: '0.5rem' }}>
                                    {alerts.length === 0 ? (
                                        <div style={{
                                            padding: '3rem 2rem',
                                            textAlign: 'center',
                                            color: 'var(--color-text-light)'
                                        }}>
                                            <FontAwesomeIcon icon={faCheckCircle} size="3x" style={{ color: '#10b981', marginBottom: '1rem' }} />
                                            <p style={{ margin: 0, fontWeight: '600' }}>Aucune notification</p>
                                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>Tout est à jour !</p>
                                        </div>
                                    ) : (
                                        alerts.map(alert => (
                                            <div
                                                key={alert.id}
                                                style={{
                                                    padding: '1rem',
                                                    margin: '0.5rem',
                                                    background: 'var(--color-background)',
                                                    borderRadius: 'var(--border-radius-md)',
                                                    borderLeft: `4px solid ${alert.color}`,
                                                    transition: 'all 0.2s',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(164, 198, 57, 0.05)'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--color-background)'}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <FontAwesomeIcon icon={alert.icon} style={{ color: alert.color }} />
                                                        <strong style={{ fontSize: '0.95rem' }}>{alert.title}</strong>
                                                    </div>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            dismissAlert(alert.id);
                                                        }}
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: 'var(--color-text-light)',
                                                            padding: '0 4px'
                                                        }}
                                                        title="Masquer cette alerte"
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} size="sm" />
                                                    </button>
                                                </div>
                                                <p style={{
                                                    margin: '0 0 0.75rem 0',
                                                    color: 'var(--color-text-light)',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    {alert.message}
                                                </p>
                                                <button
                                                    onClick={() => {
                                                        alert.action();
                                                        setShowNotifications(false);
                                                    }}
                                                    style={{
                                                        background: alert.color,
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '6px 12px',
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    Voir les entrées
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                                {alerts.length > 0 && (
                                    <div style={{
                                        padding: '1rem 1.5rem',
                                        borderTop: '1px solid var(--color-light-gray)',
                                        textAlign: 'center',
                                        position: 'sticky',
                                        bottom: 0,
                                        background: 'var(--color-surface)'
                                    }}>
                                        <button
                                            onClick={() => {
                                                setDismissedAlerts(alerts.map(a => a.id));
                                                toast.success('Toutes les alertes ont été masquées');
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid var(--color-light-gray)',
                                                color: 'var(--color-text)',
                                                padding: '8px 16px',
                                                borderRadius: '6px',
                                                fontSize: '0.9rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Masquer toutes les alertes
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>


                    {/* Advanced Search Button - Icon Only */}
                    <button
                        className={styles.addButton}
                        onClick={() => setShowAdvancedSearch(true)}
                        style={{
                            background: activeFiltersCount > 0 ? '#3b82f6' : '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            position: 'relative',
                            minWidth: '40px',
                            padding: '10px'
                        }}
                        title="Recherche avancée"
                    >
                        <FontAwesomeIcon icon={faSliders} />
                        {activeFiltersCount > 0 && (
                            <span style={{
                                position: 'absolute',
                                top: '-4px',
                                right: '-4px',
                                background: '#ef4444',
                                color: 'white',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                border: '2px solid var(--color-background)'
                            }}>
                                {activeFiltersCount}
                            </span>
                        )}
                    </button>



                    <button className={styles.addButton} onClick={handleCreate}>
                        <FontAwesomeIcon icon={faPlus} /> Saisir Heures
                    </button>
                </div>
            </div>

            {/* View Mode Toggles - Moved Below Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', width: '100%' }}>
                <div className={styles.viewToggles}>
                    <button
                        className={`${styles.toggleBtn} ${viewMode === 'list' ? styles.active : ''}`}
                        onClick={() => setViewMode('list')}
                    >
                        <FontAwesomeIcon icon={faList} /> Liste
                    </button>
                    <button
                        className={`${styles.toggleBtn} ${viewMode === 'calendar' ? styles.active : ''}`}
                        onClick={() => setViewMode('calendar')}
                    >
                        <FontAwesomeIcon icon={faCalendar} /> Calendrier
                    </button>
                    <button
                        className={`${styles.toggleBtn} ${viewMode === 'by-employee' ? styles.active : ''}`}
                        onClick={() => setViewMode('by-employee')}
                    >
                        <FontAwesomeIcon icon={faUserFriends} /> Par Employé
                    </button>
                    <button
                        className={`${styles.toggleBtn} ${viewMode === 'by-chantier' ? styles.active : ''}`}
                        onClick={() => setViewMode('by-chantier')}
                    >
                        <FontAwesomeIcon icon={faHardHat} /> Par Chantier
                    </button>
                </div>

                <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-light-gray)', padding: '4px', borderRadius: '12px', display: 'flex', gap: '4px' }}>
                    <button
                        className={`${styles.toggleBtn} ${viewMode === 'card' ? styles.active : ''}`}
                        onClick={() => setViewMode('card')}
                        title="Vue Cartes"
                        style={{ padding: '8px 12px', minWidth: 'unset', margin: 0 }}
                    >
                        <FontAwesomeIcon icon={faThLarge} />
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className={styles.statsCards} style={{ marginBottom: '2rem' }}>
                <div className={styles.card}>
                    <div className={styles.cardIcon} style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                        <FontAwesomeIcon icon={faCalendarWeek} style={{ color: '#3b82f6' }} />
                    </div>
                    <div className={styles.cardInfo}>
                        <p>Cette Semaine</p>
                        <span style={{ color: '#3b82f6' }}>{kpis.totalHoursWeek}h</span>
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardIcon} style={{ background: 'rgba(139, 92, 246, 0.1)' }}>
                        <FontAwesomeIcon icon={faCalendarDay} style={{ color: '#8b5cf6' }} />
                    </div>
                    <div className={styles.cardInfo}>
                        <p>Ce Mois</p>
                        <span style={{ color: '#8b5cf6' }}>{kpis.totalHoursMonth}h</span>
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardIcon} style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                        <FontAwesomeIcon icon={faChartBar} style={{ color: '#10b981' }} />
                    </div>
                    <div className={styles.cardInfo}>
                        <p>Moyenne/Jour</p>
                        <span style={{ color: '#10b981' }}>{kpis.avgHoursPerDay}h</span>
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardIcon}>
                        <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className={styles.cardInfo}>
                        <p>Entrées Affichées</p>
                        <span>{kpis.entriesCount} ({kpis.totalHoursFiltered}h)</span>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div style={{
                background: 'var(--color-surface)',
                padding: '1.5rem',
                borderRadius: 'var(--border-radius-lg)',
                marginBottom: '1.5rem',
                boxShadow: 'var(--shadow-light)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                    <FontAwesomeIcon icon={faFilter} style={{ color: 'var(--color-primary)' }} />
                    <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '700' }}>Filtres</h4>
                </div>

                {/* Quick Date Filters */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    {[
                        { value: 'TODAY', label: "Aujourd'hui" },
                        { value: 'THIS_WEEK', label: 'Cette Semaine' },
                        { value: 'THIS_MONTH', label: 'Ce Mois' },
                        { value: 'ALL', label: 'Tout' },
                        { value: 'CUSTOM', label: 'Personnalisé' }
                    ].map(filter => (
                        <button
                            key={filter.value}
                            onClick={() => setDateFilter(filter.value)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '6px',
                                border: '1px solid var(--color-light-gray)',
                                background: dateFilter === filter.value ? 'var(--color-primary)' : 'transparent',
                                color: dateFilter === filter.value ? 'white' : 'var(--color-text)',
                                fontWeight: '600',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>

                {/* Custom Date Range */}
                {dateFilter === 'CUSTOM' && (
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                        <div>
                            <label style={{ fontSize: '0.85rem', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
                                Du
                            </label>
                            <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                style={{
                                    padding: '8px 12px',
                                    border: '1px solid var(--color-light-gray)',
                                    borderRadius: 'var(--border-radius-md)',
                                    backgroundColor: 'var(--color-surface)',
                                    color: 'var(--color-text)'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.85rem', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
                                Au
                            </label>
                            <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                style={{
                                    padding: '8px 12px',
                                    border: '1px solid var(--color-light-gray)',
                                    borderRadius: 'var(--border-radius-md)',
                                    backgroundColor: 'var(--color-surface)',
                                    color: 'var(--color-text)'
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Search and Dropdowns */}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '200px', position: 'relative' }}>
                        <FontAwesomeIcon
                            icon={faSearch}
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'var(--color-text-light)'
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 10px 10px 40px',
                                border: '1px solid var(--color-light-gray)',
                                borderRadius: 'var(--border-radius-md)',
                                fontSize: '0.95rem',
                                backgroundColor: 'var(--color-surface)',
                                color: 'var(--color-text)'
                            }}
                        />
                    </div>
                    <select
                        value={employeeFilter}
                        onChange={(e) => setEmployeeFilter(e.target.value)}
                        style={{
                            padding: '10px 16px',
                            border: '1px solid var(--color-light-gray)',
                            borderRadius: 'var(--border-radius-md)',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            backgroundColor: 'var(--color-surface)',
                            color: 'var(--color-text)',
                            cursor: 'pointer'
                        }}
                    >
                        <option value="ALL">Tous les employés</option>
                        {employees.map(emp => (
                            <option key={emp} value={emp}>{emp}</option>
                        ))}
                    </select>
                    <select
                        value={chantierFilter}
                        onChange={(e) => setChantierFilter(e.target.value)}
                        style={{
                            padding: '10px 16px',
                            border: '1px solid var(--color-light-gray)',
                            borderRadius: 'var(--border-radius-md)',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            backgroundColor: 'var(--color-surface)',
                            color: 'var(--color-text)',
                            cursor: 'pointer'
                        }}
                    >
                        <option value="ALL">Tous les chantiers</option>
                        {chantiers.map(ch => (
                            <option key={ch} value={ch}>{ch}</option>
                        ))}
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{
                            padding: '10px 16px',
                            border: '1px solid var(--color-light-gray)',
                            borderRadius: 'var(--border-radius-md)',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            backgroundColor: 'var(--color-surface)',
                            color: 'var(--color-text)',
                            cursor: 'pointer'
                        }}
                    >
                        <option value="ALL">Tous les statuts</option>
                        <option value="pending">À valider</option>
                        <option value="approved">Validé</option>
                        <option value="rejected">Rejeté</option>
                    </select>
                </div>
            </div>

            {/* Views */}
            {
                loading ? (
                    <div>Chargement...</div>
                ) : (
                    <>
                        {viewMode === 'list' && (
                            <TimesheetListView
                                filteredEntries={groupedByDayEntries}
                                selectedEntries={selectedEntries}
                                setSelectedEntries={setSelectedEntries}
                                toggleSelectAll={toggleSelectAll}
                                toggleSelectEntry={toggleSelectEntry}
                                handleBulkApprove={handleBulkApprove}
                                handleBulkReject={handleBulkReject}
                                handleSingleApprove={handleSingleApprove}
                                handleSingleReject={handleSingleReject}
                                handleEdit={handleEdit}
                                handleDelete={handleDelete}
                                isWeekend={isWeekend}
                                getHoursColor={getHoursColor}
                                getStatusBadge={getStatusBadge}
                                searchTerm={searchTerm}
                                dateFilter={dateFilter}
                                employeeFilter={employeeFilter}
                                chantierFilter={chantierFilter}
                            />
                        )}
                        {viewMode === 'card' && (
                            <TimesheetCardView
                                filteredEntries={groupedByDayEntries}
                                selectedEntries={selectedEntries}
                                setSelectedEntries={setSelectedEntries}
                                toggleSelectAll={toggleSelectAll}
                                toggleSelectEntry={toggleSelectEntry}
                                handleBulkApprove={handleBulkApprove}
                                handleBulkReject={handleBulkReject}
                                handleSingleApprove={handleSingleApprove}
                                handleSingleReject={handleSingleReject}
                                handleEdit={handleEdit}
                                handleDelete={handleDelete}
                                isWeekend={isWeekend}
                                getHoursColor={getHoursColor}
                                getStatusBadge={getStatusBadge}
                                searchTerm={searchTerm}
                                dateFilter={dateFilter}
                                employeeFilter={employeeFilter}
                                chantierFilter={chantierFilter}
                            />
                        )}
                        {viewMode === 'calendar' && (
                            <TimesheetCalendarView
                                filteredEntries={filteredEntries}
                                localizer={localizer}
                                setEditingEntry={setEditingEntry}
                                setIsFormOpen={setIsFormOpen}
                            />
                        )}
                        {viewMode === 'by-employee' && (
                            <TimesheetEmployeeView
                                groupedByEmployee={groupedByEmployee}
                                getHoursColor={getHoursColor}
                                handleEdit={handleEdit}
                                handleDelete={handleDelete}
                            />
                        )}
                        {viewMode === 'by-chantier' && (
                            <TimesheetChantierView
                                groupedByChantier={groupedByChantier}
                                getHoursColor={getHoursColor}
                                handleEdit={handleEdit}
                                handleDelete={handleDelete}
                            />
                        )}
                    </>
                )
            }

            <TimesheetBulkRejectModal
                showValidationModal={showValidationModal}
                setShowValidationModal={setShowValidationModal}
                selectedEntries={selectedEntries}
                validationComment={validationComment}
                setValidationComment={setValidationComment}
                confirmBulkReject={confirmBulkReject}
            />

            <TimesheetStatsModal
                showStats={showStats}
                setShowStats={setShowStats}
                chartData={chartData}
                entries={entries}
            />

            <TimesheetTimerPanel
                showTimerPanel={showTimerPanel}
                setShowTimerPanel={setShowTimerPanel}
                timerRunning={timerRunning}
                timerElapsed={timerElapsed}
                formatTime={formatTime}
                locationStatus={locationStatus}
                userLocation={userLocation}
                nearestSite={nearestSite}
                startTimer={startTimer}
                pauseTimer={pauseTimer}
                resumeTimer={resumeTimer}
                stopTimer={stopTimer}
                timerChantier={timerChantier}
                setTimerChantier={setTimerChantier}
                chantiers={chantiers}
                timerDescription={timerDescription}
                setTimerDescription={setTimerDescription}
                saveTimer={saveTimer}
            />

            <TimesheetAdvancedSearch
                showAdvancedSearch={showAdvancedSearch}
                setShowAdvancedSearch={setShowAdvancedSearch}
                advancedFilters={advancedFilters}
                setAdvancedFilters={setAdvancedFilters}
                employees={employees}
                chantiers={chantiers}
                resetAdvancedFilters={resetAdvancedFilters}
                applyAdvancedFilters={applyAdvancedFilters}
                activeFiltersCount={activeFiltersCount}
            />

            <TimesheetPreferencesModal
                showPreferences={showPreferences}
                setShowPreferences={setShowPreferences}
                userPreferences={userPreferences}
                setUserPreferences={setUserPreferences}
                resetPreferences={resetPreferences}
                savePreferences={savePreferences}
            />

            {
                isFormOpen && (
                    <TimesheetForm
                        entry={editingEntry}
                        userRole={userRole}
                        onSave={handleSave}
                        onCancel={() => setIsFormOpen(false)}
                    />
                )
            }
        </div >
    );
};

export default Timesheets;
